import { acceptHMRUpdate, defineStore } from 'pinia'
import { ref } from 'vue'
import { useUserStore } from './user'
import type { LoadContainerBasicOptions } from '~/services/apiHelpers'
import type { GETContainer, GETContainerBasic } from '~/services/apiClient'
import { useGlobalFilters } from '~/compositions/globalFilters'
import { BasicContainer } from '~/models/containers'
import {
  loadContainersBasic,
  loadContainersBasicWithUnwatchedAndAutoBook,
} from '~/services/apiHelpers'

export const usedBasicContainersStore = defineStore('basicContainers', () => {
  const userStore = useUserStore()
  const containers = ref([] as BasicContainer[])
  const containersLookup = ref(new Map<string, BasicContainer>())
  // The three reasons a searched container can be missing from the rows, classified by
  // the backend (see /containers/basic/with_unwatched/). All only populated by
  // loadWithUnwatched(). Containers excluded by a filter the user set themselves are
  // deliberately in none of these — an explicit filter needs no explanation.
  //
  // Searched container numbers this org doesn't track, surfaced as the Empties
  // "Start tracking" banner/CTA.
  const unwatchedContainers = ref([] as string[])
  // Tracked, but at a port complex outside the active port filter.
  const containersWatchedAtOtherPorts = ref([] as string[])
  // Tracked at the right port, but not currently in an empties cycle state.
  const containersNotCurrentlyEmpty = ref([] as string[])
  const loading = ref(false)
  const { portComplexes } = useGlobalFilters()

  function setContainers(basic: GETContainerBasic[]) {
    containers.value = basic.map(
      (c) => new BasicContainer(c, userStore.demo_mode)
    )
    containersLookup.value = new Map(containers.value.map((c) => [c.number, c]))
  }

  function clear() {
    containers.value = []
    containersLookup.value = new Map()
    unwatchedContainers.value = []
    containersWatchedAtOtherPorts.value = []
    containersNotCurrentlyEmpty.value = []
  }

  // Plain basic-container load (bare array). Used everywhere except the Empties list.
  function load(options: LoadContainerBasicOptions) {
    loading.value = true
    return loadContainersBasic({
      ...options,
      port_complexes: portComplexes.value,
    })
      .then((resp) => setContainers(resp.data))
      .finally(() => {
        loading.value = false
      })
  }

  // Empties list view only: also reports which searched containers aren't tracked,
  // and carries each container's empty auto-book status inline (no separate
  // /list/minimal/ call). Other callers must use load() so they stay on the lighter
  // /containers/basic/ endpoint.
  function loadWithUnwatched(options: LoadContainerBasicOptions) {
    loading.value = true
    return loadContainersBasicWithUnwatchedAndAutoBook({
      ...options,
      port_complexes: portComplexes.value,
    })
      .then((resp) => {
        setContainers(resp.data.containers)
        unwatchedContainers.value = resp.data.unwatched_containers
        // Optional in the generated client (they default to [] server-side), so
        // normalize to an array rather than leaking undefined to every consumer.
        containersWatchedAtOtherPorts.value =
          resp.data.containers_watched_at_other_ports ?? []
        containersNotCurrentlyEmpty.value =
          resp.data.containers_not_currently_empty ?? []
      })
      .finally(() => {
        loading.value = false
      })
  }
  function addContainers(newContainers: BasicContainer[]) {
    for (const c of newContainers) {
      const existing = containersLookup.value.get(c.number)
      if (existing) {
        const idx = containers.value.indexOf(existing)
        if (idx !== -1) containers.value[idx] = c
      } else {
        containers.value.push(c)
      }
      containersLookup.value.set(c.number, c)
    }
  }
  function updateFromGETContainer(raw: GETContainer) {
    if (!containersLookup.value.has(raw.container_number)) return
    const basicData = {
      container_number: raw.container_number,
      cycle_state: raw.cycle_state,
      shipping_line: raw.shipping_line,
      container_type: raw.container_type,
      last_related_terminal: raw.last_related_terminal,
      import_out_gated_time: raw.import_out_gated_time,
      empty_appointment_time: raw.empty_appointment_time,
      empty_in_gated_time: raw.empty_in_gated_time,
      customer: raw.customer,
      tags: raw.tags,
      vessel: raw.vessel,
      vessel_eta: raw.vessel_eta,
    }
    const updated = new BasicContainer(basicData, userStore.demo_mode)
    addContainers([updated])
  }
  function sortByOutGateTime() {
    containers.value.sort((a, b) => {
      if (a.import_out_gated_time && b.import_out_gated_time) {
        return a.import_out_gated_time > b.import_out_gated_time ? 1 : -1
      } else {
        return 0
      }
    })
  }
  return {
    containers,
    containersLookup,
    unwatchedContainers,
    containersWatchedAtOtherPorts,
    containersNotCurrentlyEmpty,
    loading,
    clear,
    load,
    loadWithUnwatched,
    addContainers,
    updateFromGETContainer,
    sortByOutGateTime,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(usedBasicContainersStore, import.meta.hot)
  )
}
